import { tooltipClasses, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Skeleton from '@mui/material/Skeleton';

import { colors } from '@/theme/colors';
import { useApiQuery } from '@/hooks/useApi';
import { ThemedTooltip } from '@/components/chat/FeedbackContainer';
import { memo, useMemo } from 'react';
import { queryTableNameUtil } from '@/util/queryTableNameUtil';
import { useProfile } from '@/hooks/useProfile';

type Props = {
  handleSendMessage: (message?: string, suggestion?: any) => void;
};

const SuggestionSkeleton = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderRadius: '8px',
        width: '20%',
        padding: '8px 12px',
        boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Skeleton variant="text" width="90%" height={28} />
    </Box>
  );
};

const ChatSuggestions: React.FC<Props> = memo(({ handleSendMessage }) => {
  const theme = useTheme();
  const { profile } = useProfile();
  const expSystemKey = profile?.value[0].eXpSystemKey!;

  const { data, isLoading } = useApiQuery<any>(
    ['/user/prompts', 'isConversationStarter'],
    '/user/prompts',
    {
      refetchOnWindowFocus: false,
      staleTime: Infinity,
    },
    false,
    {
      isConversationStarter: 'true',
      isActive: 'true',
    },
  );
  const randomSuggestions = useMemo<any[]>(() => {
    if (!data?.data) return [];
    if (data.data.length < 4) return [];
    if (data.data.length === 4) return data.data;
    return [...data.data].sort(() => Math.random() - 0.5).slice(0, 4);
  }, [data]);
  const suggestionHandler = (suggestion: any) => {
    const { sqlQuery, tableName } = queryTableNameUtil(
      suggestion.sqlQuery,
      suggestion.tableName,
      expSystemKey,
    );
    handleSendMessage(suggestion.question, {
      ...suggestion,
      sqlQuery,
      tableName,
    });
  };
  return (
    <Box
      data-testid="chat-suggestions"
      sx={{
        width: '96%',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '10px',
        justifyContent: 'center',
        alignItems: 'center',
        pb: 2,
      }}
    >
      {isLoading
        ? Array.from({ length: 4 }).map((_, index) => (
            <SuggestionSkeleton key={index} />
          ))
        : randomSuggestions?.map((s: any, index: number) => (
            <ThemedTooltip
              key={s.id}
              title={s.question}
              placement="top"
              sx={{
                [`& .${tooltipClasses.tooltip}`]: {
                  textTransform: 'none',
                },
              }}
            >
              <Button
                data-testid={`chat-suggestion-${s.id}`}
                sx={{
                  maxWidth: '90%',
                  backgroundColor: theme.palette.background.paper,
                  borderRadius: '8px',
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                  color: theme.palette.text.primary,
                  transition:
                    'background-color 0.2s ease-in-out, transform 0.1s ease-in-out',
                  '&:hover': {
                    color: colors.black,
                    backgroundColor: colors.mistGray,
                    transform: 'scale(1.04)',
                  },
                }}
                onClick={() => suggestionHandler(s)}
              >
                <Typography
                  sx={{
                    fontSize: '14px',
                    textAlign: 'left',
                    textTransform: 'none',
                  }}
                >
                  {s.question}
                </Typography>
              </Button>
            </ThemedTooltip>
          ))}
    </Box>
  );
});

export default ChatSuggestions;
